import {
	arrow,
	autoPlacement,
	flip,
	type Middleware,
	offset,
	shift,
	size,
	type Padding,
} from "@floating-ui/react";
import type { RefObject } from "react";
import type {
	ContentTooltipAlign,
	ContentTooltipCollisionPadding,
	ContentTooltipSide,
} from "./content-tooltip-types";

export interface BuildContentTooltipMiddlewareInput {
	side?: ContentTooltipSide;
	align?: ContentTooltipAlign;
	sideOffset?: number;
	collisionPadding?: ContentTooltipCollisionPadding;
	autoPlace?: boolean;
	followCursor?: boolean;
	/** When set with an element ref, adds Floating UI arrow middleware. */
	arrowRef?: RefObject<HTMLElement | null>;
	showArrow?: boolean;
}

function paddingValue(
	collisionPadding: ContentTooltipCollisionPadding | undefined,
): number | Padding {
	if (collisionPadding == null) {
		return 8;
	}
	return collisionPadding;
}

/**
 * Floating UI middleware stack for ContentTooltip.
 * - No `side` + autoPlace → autoPlacement + shift
 * - Explicit `side` + autoPlace → flip + shift (prefer side)
 * - autoPlace false → offset only (fixed preference)
 * - showArrow → arrow middleware when arrowRef provided
 */
export function buildContentTooltipMiddleware({
	side,
	sideOffset = 10,
	collisionPadding,
	autoPlace = true,
	followCursor = false,
	arrowRef,
	showArrow = false,
}: BuildContentTooltipMiddlewareInput): Middleware[] {
	const padding = paddingValue(collisionPadding);
	const middleware: Middleware[] = [offset(sideOffset)];

	const pushArrow = () => {
		if (showArrow && arrowRef != null) {
			middleware.push(arrow({ element: arrowRef, padding: 6 }));
		}
	};

	if (!autoPlace) {
		pushArrow();
		return middleware;
	}

	// Cursor-follow still shifts/flips so the tip stays in-window.
	if (followCursor) {
		middleware.push(flip({ padding }), shift({ padding }));
		pushArrow();
		return middleware;
	}

	if (side == null) {
		middleware.push(
			autoPlacement({
				padding: typeof padding === "number" ? padding : 8,
			}),
			shift({ padding }),
			size({
				padding: typeof padding === "number" ? padding : 8,
				apply({ availableWidth, availableHeight, elements }) {
					Object.assign(elements.floating.style, {
						maxWidth: `${Math.max(0, availableWidth)}px`,
						maxHeight: `${Math.max(0, availableHeight)}px`,
					});
				},
			}),
		);
		pushArrow();
		return middleware;
	}

	middleware.push(flip({ padding }), shift({ padding }));
	pushArrow();
	return middleware;
}

export function contentTooltipPlacement(
	side: ContentTooltipSide | undefined,
	align: ContentTooltipAlign | undefined,
):
	| ContentTooltipSide
	| `${ContentTooltipSide}-${Exclude<ContentTooltipAlign, "center">}` {
	if (side == null) {
		return "top";
	}
	if (align == null || align === "center") {
		return side;
	}
	return `${side}-${align}`;
}
